import React, { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { purchaseReturnApi, purchaseApi, vendorApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import Modal from "./Modal";
import ReturnVoucherModal from "./ReturnVoucherModal";
import {
  FiRotateCcw,
  FiPlus,
  FiSearch,
  FiUpload,
  FiDownload,
  FiFileText,
  FiPrinter,
  FiTrash2,
  FiCheckCircle,
  FiAlertTriangle,
  FiX,
  FiClock,
  FiTruck,
  FiBox,
  FiChevronLeft,
  FiChevronRight,
  FiLayers,
} from "react-icons/fi";
import { toast } from "react-toastify";

const PurchaseReturnsTab = ({ vendors = [], purchases = [], onReturnChange }) => {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || (Array.isArray(user?.permissions) && user.permissions.includes("purchases"));

  // View state: 'list' | 'create'
  const [viewMode, setViewMode] = useState("list");

  // Returns list state
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedVendorFilter, setSelectedVendorFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Selected return for viewing voucher
  const [viewingReturn, setViewingReturn] = useState(null);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, returnItem: null, deleting: false });

  // --------------------------------------------------------------------------
  // CREATE RETURN STATE
  // --------------------------------------------------------------------------
  const [selectedBillNumber, setSelectedBillNumber] = useState("");
  const [billSearchTerm, setBillSearchTerm] = useState("");
  const [isBillDropdownOpen, setIsBillDropdownOpen] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 10));
  const [returnNumber, setReturnNumber] = useState("");
  const [reason, setReason] = useState("");
  const [loadingBillItems, setLoadingBillItems] = useState(false);

  // Items from selected bill
  const [billItems, setBillItems] = useState([]);
  // Selected items to return:
  // For serial: Set of inventory_item_id or serial_numbers
  const [selectedSerialIds, setSelectedSerialIds] = useState(new Set());
  // For non-serial: Map of inventory_item_id -> returnQty
  const [nonSerialReturnQty, setNonSerialReturnQty] = useState({});
  // Item specific reasons: Map of inventory_item_id -> reason string
  const [itemReasons, setItemReasons] = useState({});

  // Sheet upload state
  const sheetInputRef = useRef(null);
  const [parsingSheet, setParsingSheet] = useState(false);
  const [sheetUploadFeedback, setSheetUploadFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch returns list
  const fetchReturns = async () => {
    try {
      setLoading(true);
      const res = await purchaseReturnApi.getAll();
      setReturns(res.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load purchase returns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReturns();
  }, []);

  // Fetch items when bill_number changes
  const handleSelectBill = async (billNum) => {
    setSelectedBillNumber(billNum);
    setIsBillDropdownOpen(false);
    setBillSearchTerm(billNum);
    setBillItems([]);
    setSelectedSerialIds(new Set());
    setNonSerialReturnQty({});
    setItemReasons({});
    setSheetUploadFeedback(null);

    if (!billNum) return;

    try {
      setLoadingBillItems(true);
      const res = await purchaseReturnApi.getBillReturnableItems(billNum);
      const data = res.data;
      if (data && data.items) {
        setBillItems(data.items);
        if (data.purchase && data.purchase.vendor_id) {
          setSelectedVendorId(String(data.purchase.vendor_id));
        }
      }
    } catch (err) {
      toast.error(err.message || `Failed to fetch items for Bill #${billNum}`);
    } finally {
      setLoadingBillItems(false);
    }
  };

  // Filter bills for autocomplete dropdown
  const filteredBills = useMemo(() => {
    if (!purchases || purchases.length === 0) return [];
    if (!billSearchTerm.trim()) return purchases.slice(0, 30);
    const term = billSearchTerm.toLowerCase();
    return purchases.filter((p) => {
      const bMatch = p.bill_number && p.bill_number.toLowerCase().includes(term);
      const vMatch = p.vendor_name && p.vendor_name.toLowerCase().includes(term);
      return bMatch || vMatch;
    }).slice(0, 30);
  }, [purchases, billSearchTerm]);

  // Handle Serial checkbox toggle
  const toggleSerialSelect = (itemId) => {
    setSelectedSerialIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Select all eligible serial items
  const toggleSelectAllEligibleSerials = () => {
    const eligibleSerialItems = billItems.filter((it) => it.is_serial && it.returnable);
    const allSelected = eligibleSerialItems.every((it) => selectedSerialIds.has(it.id));

    if (allSelected) {
      setSelectedSerialIds((prev) => {
        const next = new Set(prev);
        eligibleSerialItems.forEach((it) => next.delete(it.id));
        return next;
      });
    } else {
      setSelectedSerialIds((prev) => {
        const next = new Set(prev);
        eligibleSerialItems.forEach((it) => next.add(it.id));
        return next;
      });
    }
  };

  // Handle Non-serial return quantity change
  const handleNonSerialQtyChange = (itemId, maxReturnable, val) => {
    const parsed = parseInt(val) || 0;
    const clamped = Math.max(0, Math.min(maxReturnable, parsed));
    setNonSerialReturnQty((prev) => ({
      ...prev,
      [itemId]: clamped,
    }));
  };

  // Handle item reason change
  const handleItemReasonChange = (itemId, val) => {
    setItemReasons((prev) => ({
      ...prev,
      [itemId]: val,
    }));
  };

  // Calculate return total items and total amount
  const returnSummary = useMemo(() => {
    let itemsCount = 0;
    let totalAmt = 0.0;

    billItems.forEach((it) => {
      if (it.is_serial) {
        if (selectedSerialIds.has(it.id)) {
          itemsCount += 1;
          totalAmt += parseFloat(it.unit_price) || 0.0;
        }
      } else {
        const qty = nonSerialReturnQty[it.id] || 0;
        if (qty > 0) {
          itemsCount += qty;
          totalAmt += qty * (parseFloat(it.unit_price) || 0.0);
        }
      }
    });

    return { itemsCount, totalAmt };
  }, [billItems, selectedSerialIds, nonSerialReturnQty]);

  // --------------------------------------------------------------------------
  // SHEET UPLOAD FOR RETURN
  // --------------------------------------------------------------------------
  const handleSheetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!selectedBillNumber) {
      toast.warning("Please select or enter the Bill Number first before uploading sheet");
      if (sheetInputRef.current) sheetInputRef.current.value = "";
      return;
    }

    try {
      setParsingSheet(true);
      const formData = new FormData();
      formData.append("sheet", file);
      formData.append("bill_number", selectedBillNumber);

      const res = await purchaseReturnApi.parseSheet(formData);
      const { matched_items = [], unmatched_rows = [] } = res.data || {};

      let newlySelectedSerials = 0;
      const nextSerialSet = new Set(selectedSerialIds);
      const nextNonSerialQty = { ...nonSerialReturnQty };
      const nextReasons = { ...itemReasons };

      matchedItemsLoop: for (const m of matched_items) {
        if (m.status === "error") continue;

        if (m.serial_number) {
          const foundItem = billItems.find(
            (b) => b.serial_number && b.serial_number.toLowerCase().trim() === m.serial_number.toLowerCase().trim()
          );
          if (foundItem && foundItem.returnable) {
            nextSerialSet.add(foundItem.id);
            if (m.reason) nextReasons[foundItem.id] = m.reason;
            newlySelectedSerials++;
          }
        } else if (m.item_name) {
          const foundItem = billItems.find(
            (b) => !b.is_serial && b.item_name.toLowerCase().trim() === m.item_name.toLowerCase().trim()
          );
          if (foundItem && foundItem.returnable) {
            const currentVal = nextNonSerialQty[foundItem.id] || 0;
            const newTotal = Math.min(foundItem.returnable_qty, currentVal + (m.quantity || 1));
            nextNonSerialQty[foundItem.id] = newTotal;
            if (m.reason) nextReasons[foundItem.id] = m.reason;
          }
        }
      }

      setSelectedSerialIds(nextSerialSet);
      setNonSerialReturnQty(nextNonSerialQty);
      setItemReasons(nextReasons);

      setSheetUploadFeedback({
        total: matched_items.length + unmatched_rows.length,
        matched: matched_items.length,
        unmatched: unmatched_rows,
      });

      toast.success(`Sheet matched: ${matched_items.length} item(s) selected for return!`);
    } catch (err) {
      toast.error(err.message || "Failed to parse return sheet");
    } finally {
      setParsingSheet(false);
      if (sheetInputRef.current) sheetInputRef.current.value = "";
    }
  };

  // Download Sample Template
  const handleDownloadTemplate = () => {
    const sampleData = [
      {
        "Serial Number": "SN-DELL-88392",
        "Item Name": "",
        Quantity: 1,
        Reason: "Defective display port",
      },
      {
        "Serial Number": "SN-DELL-88393",
        "Item Name": "",
        Quantity: 1,
        Reason: "Wrong specs",
      },
      {
        "Serial Number": "",
        "Item Name": "Mouse Pad",
        Quantity: 5,
        Reason: "Excess quantity received",
      },
      {
        "Serial Number": "",
        "Item Name": "RAM - GB",
        Quantity: 2,
        Reason: "Damaged packaging",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchase_Return_Template");
    XLSX.writeFile(wb, "Purchase_Return_Sample_Template.xlsx");
    toast.info("Sample Return Template downloaded");
  };

  // Submit Purchase Return
  const handleSubmitReturn = async (e) => {
    e.preventDefault();

    if (!selectedBillNumber) {
      toast.error("Please enter or select a Bill Number");
      return;
    }

    if (returnSummary.itemsCount === 0) {
      toast.error("Please select at least 1 item to return (or specify a return quantity)");
      return;
    }

    const itemsPayload = [];

    billItems.forEach((it) => {
      if (it.is_serial) {
        if (selectedSerialIds.has(it.id)) {
          itemsPayload.push({
            inventory_item_id: it.id,
            item_name: it.item_name,
            serial_number: it.serial_number,
            category_id: it.category_id,
            sub_category_id: it.sub_category_id,
            quantity: 1,
            unit_price: parseFloat(it.unit_price) || 0.0,
            reason: itemReasons[it.id] || reason || "",
          });
        }
      } else {
        const qty = nonSerialReturnQty[it.id] || 0;
        if (qty > 0) {
          itemsPayload.push({
            inventory_item_id: it.id,
            item_name: it.item_name,
            serial_number: null,
            category_id: it.category_id,
            sub_category_id: it.sub_category_id,
            quantity: qty,
            unit_price: parseFloat(it.unit_price) || 0.0,
            reason: itemReasons[it.id] || reason || "",
          });
        }
      }
    });

    const payload = {
      bill_number: selectedBillNumber,
      vendor_id: selectedVendorId,
      return_number: returnNumber || undefined,
      return_date: returnDate,
      reason: reason || undefined,
      items: itemsPayload,
    };

    try {
      setSubmitting(true);
      const res = await purchaseReturnApi.create(payload);
      toast.success(res.message || "Purchase return recorded successfully!");

      // Open voucher modal immediately for printing
      if (res.data) {
        setViewingReturn(res.data);
      }

      // Reset form & reload returns list
      setSelectedBillNumber("");
      setBillSearchTerm("");
      setBillItems([]);
      setSelectedSerialIds(new Set());
      setNonSerialReturnQty({});
      setItemReasons({});
      setReason("");
      setReturnNumber("");
      setSheetUploadFeedback(null);
      setViewMode("list");
      fetchReturns();
      if (typeof onReturnChange === "function") {
        onReturnChange();
      }
    } catch (err) {
      toast.error(err.message || "Failed to record purchase return");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete return
  const handleDeleteReturn = async () => {
    if (!deleteModal.returnItem) return;
    try {
      setDeleteModal((prev) => ({ ...prev, deleting: true }));
      await purchaseReturnApi.delete(deleteModal.returnItem.id);
      toast.success("Purchase return deleted and items restored to stock successfully");
      setDeleteModal({ open: false, returnItem: null, deleting: false });
      fetchReturns();
      if (typeof onReturnChange === "function") {
        onReturnChange();
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete purchase return");
      setDeleteModal((prev) => ({ ...prev, deleting: false }));
    }
  };

  // Filtered returns list
  const filteredReturns = useMemo(() => {
    return returns.filter((r) => {
      const matchSearch =
        !search.trim() ||
        (r.return_number && r.return_number.toLowerCase().includes(search.toLowerCase())) ||
        (r.bill_number && r.bill_number.toLowerCase().includes(search.toLowerCase())) ||
        (r.vendor_name && r.vendor_name.toLowerCase().includes(search.toLowerCase()));

      const matchVendor = !selectedVendorFilter || String(r.vendor_id) === String(selectedVendorFilter);
      const matchFrom = !dateFrom || (r.return_date && r.return_date >= dateFrom);
      const matchTo = !dateTo || (r.return_date && r.return_date <= dateTo);

      return matchSearch && matchVendor && matchFrom && matchTo;
    });
  }, [returns, search, selectedVendorFilter, dateFrom, dateTo]);

  // Pagination slice
  const paginatedReturns = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredReturns.slice(start, start + pageSize);
  }, [filteredReturns, page, pageSize]);

  const totalPages = Math.ceil(filteredReturns.length / pageSize) || 1;

  // Stats
  const totalReturnAmount = useMemo(() => {
    return returns.reduce((sum, r) => sum + (parseFloat(r.total_amount) || 0), 0);
  }, [returns]);

  return (
    <div>
      {/* Return Voucher View / Print Modal */}
      {viewingReturn && (
        <ReturnVoucherModal
          isOpen={Boolean(viewingReturn)}
          onClose={() => setViewingReturn(null)}
          returnData={viewingReturn}
          type="purchase"
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, returnItem: null, deleting: false })}
        title="Confirm Delete Purchase Return"
      >
        <div style={{ padding: "10px 0" }}>
          <p style={{ fontSize: "0.92rem", color: "var(--slate-700)", marginBottom: 12 }}>
            Are you sure you want to delete Purchase Return{" "}
            <strong>{deleteModal.returnItem?.return_number}</strong>?
          </p>
          <div
            style={{
              padding: "12px 16px",
              backgroundColor: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              fontSize: "0.82rem",
              color: "#991B1B",
              marginBottom: 16,
            }}
          >
            ⚠️ <strong>Inventory Restoration:</strong> Deleting this return will restore all returned serialized and bulk
            items back to active inventory stock under Bill #{deleteModal.returnItem?.bill_number}.
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              disabled={deleteModal.deleting}
              onClick={() => setDeleteModal({ open: false, returnItem: null, deleting: false })}
              style={{
                padding: "8px 18px",
                borderRadius: 8,
                border: "1px solid var(--slate-300)",
                backgroundColor: "#fff",
                color: "var(--slate-700)",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={deleteModal.deleting}
              onClick={handleDeleteReturn}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 20px",
                borderRadius: 8,
                border: "none",
                backgroundColor: "#DC2626",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.85rem",
                cursor: deleteModal.deleting ? "not-allowed" : "pointer",
              }}
            >
              <FiTrash2 size={15} />
              {deleteModal.deleting ? "Deleting & Restoring..." : "Yes, Delete & Restore Stock"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Top Action Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h3 style={{ fontSize: "1.18rem", fontWeight: 800, color: "var(--navy)", margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <FiRotateCcw style={{ color: "#B45309" }} />
            Purchase Returns (Vendor Returns)
          </h3>
          <p style={{ fontSize: "0.82rem", color: "var(--slate-500)", margin: "4px 0 0 0" }}>
            Return purchased items back to vendors against Bill No, upload return sheets, and adjust inventory automatically.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          {viewMode === "list" ? (
            canEdit && (
              <button
                type="button"
                onClick={() => setViewMode("create")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 20px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: "#B45309",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.86rem",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(180, 83, 9, 0.25)",
                }}
              >
                <FiPlus size={16} /> + Create Purchase Return
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={() => setViewMode("list")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 18px",
                borderRadius: 8,
                border: "1.5px solid var(--slate-300)",
                backgroundColor: "#fff",
                color: "var(--slate-700)",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              ← Back to Returns List
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* VIEW: CREATE PURCHASE RETURN FORM                                         */}
      {/* ========================================================================= */}
      {viewMode === "create" ? (
        <form onSubmit={handleSubmitReturn} className="fade-in">
          {/* Step 1: Bill & Vendor Information */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg, 12px)",
              border: "1px solid var(--slate-200)",
              padding: "22px 24px",
              marginBottom: 20,
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: "#FEF3C7",
                  color: "#B45309",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiTruck size={20} />
              </div>
              <div>
                <h4 style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                  1. Select Purchase Bill & Vendor Details
                </h4>
                <div style={{ fontSize: "0.78rem", color: "var(--slate-500)" }}>
                  Enter or choose the original purchase Bill Number to load all purchased items
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 18,
              }}
            >
              {/* Bill Number Search / Autocomplete */}
              <div style={{ position: "relative" }}>
                <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                  Bill / Invoice # *
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    required
                    placeholder="Type or select Bill # (e.g. BILL-2026-001)"
                    value={billSearchTerm}
                    onChange={(e) => {
                      setBillSearchTerm(e.target.value);
                      setIsBillDropdownOpen(true);
                    }}
                    onFocus={() => setIsBillDropdownOpen(true)}
                    style={{
                      width: "100%",
                      height: 42,
                      padding: "9px 34px 9px 14px",
                      borderRadius: 8,
                      border: "1.5px solid var(--slate-300)",
                      fontSize: "0.86rem",
                      fontWeight: 600,
                      boxSizing: "border-box",
                      color: "var(--navy)",
                    }}
                  />
                  <FiSearch
                    size={15}
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--slate-400)",
                      pointerEvents: "none",
                    }}
                  />
                </div>

                {/* Autocomplete dropdown */}
                {isBillDropdownOpen && filteredBills.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: "#fff",
                      border: "1px solid var(--slate-200)",
                      borderRadius: 8,
                      boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                      maxHeight: 260,
                      overflowY: "auto",
                    }}
                  >
                    {filteredBills.map((p) => (
                      <div
                        key={p.id || p.bill_number}
                        onClick={() => handleSelectBill(p.bill_number)}
                        style={{
                          padding: "10px 14px",
                          borderBottom: "1px solid var(--slate-100)",
                          cursor: "pointer",
                          fontSize: "0.82rem",
                          backgroundColor: selectedBillNumber === p.bill_number ? "#FEF3C7" : "transparent",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F8FAFC")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = selectedBillNumber === p.bill_number ? "#FEF3C7" : "transparent")}
                      >
                        <div style={{ fontWeight: 800, color: "var(--navy)", display: "flex", justifyContent: "space-between" }}>
                          <span>#{p.bill_number}</span>
                          <span style={{ color: "#B45309", fontSize: "0.75rem" }}>
                            PKR {parseFloat(p.total_amount || 0).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: "0.74rem", color: "var(--slate-500)", marginTop: 2 }}>
                          Vendor: <strong>{p.vendor_name || "Unknown"}</strong> • Date: {p.purchase_date || p.created_at?.slice(0, 10)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Vendor Supplier (auto-selected or editable) */}
              <div>
                <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                  Vendor / Supplier *
                </label>
                <select
                  required
                  value={selectedVendorId}
                  onChange={(e) => setSelectedVendorId(e.target.value)}
                  style={{
                    width: "100%",
                    height: 42,
                    padding: "9px 14px",
                    borderRadius: 8,
                    border: "1.5px solid var(--slate-300)",
                    fontSize: "0.86rem",
                    fontWeight: 600,
                    backgroundColor: "#fff",
                    color: "var(--navy)",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">-- Choose Vendor Supplier --</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.company_name} {v.contact_person_name ? `(${v.contact_person_name})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Return Date */}
              <div>
                <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                  Return Date *
                </label>
                <input
                  type="date"
                  required
                  value={returnDate}
                  onChange={(e) => setReturnDate(e.target.value)}
                  style={{
                    width: "100%",
                    height: 42,
                    padding: "9px 14px",
                    borderRadius: 8,
                    border: "1.5px solid var(--slate-300)",
                    fontSize: "0.86rem",
                    fontWeight: 600,
                    color: "var(--navy)",
                    boxSizing: "border-box",
                    backgroundColor: "#fff",
                  }}
                />
              </div>

              {/* Custom Return # (optional) */}
              <div>
                <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                  Return Voucher # <span style={{ fontSize: "0.74rem", color: "var(--slate-400)" }}>(Auto-generated if empty)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. PR-20261014-001"
                  value={returnNumber}
                  onChange={(e) => setReturnNumber(e.target.value)}
                  style={{
                    width: "100%",
                    height: 42,
                    padding: "9px 14px",
                    borderRadius: 8,
                    border: "1.5px solid var(--slate-300)",
                    fontSize: "0.86rem",
                    boxSizing: "border-box",
                    backgroundColor: "#fff",
                  }}
                />
              </div>
            </div>

            {/* General Return Reason */}
            <div style={{ marginTop: 16 }}>
              <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                General Return Reason / Notes
              </label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      setReason((prev) => (prev ? `${prev}; ${e.target.value}` : e.target.value));
                    }
                  }}
                  style={{
                    height: 40,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: "1px solid var(--slate-300)",
                    fontSize: "0.82rem",
                    backgroundColor: "var(--slate-50)",
                    color: "var(--slate-700)",
                  }}
                >
                  <option value="">Quick Reason Presets...</option>
                  <option value="Defective / Hardware Failure">Defective / Hardware Failure</option>
                  <option value="Wrong Specifications Received">Wrong Specifications Received</option>
                  <option value="Damaged in Shipping / Transit">Damaged in Shipping / Transit</option>
                  <option value="Excess Order Return">Excess Order Return</option>
                  <option value="Vendor Quality Recall">Vendor Quality Recall</option>
                </select>

                <input
                  type="text"
                  placeholder="Enter return remarks, RMA number, or vendor agreement notes..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: 260,
                    height: 40,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1.5px solid var(--slate-300)",
                    fontSize: "0.84rem",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Step 2: Items from this Bill & Sheet Upload */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg, 12px)",
              border: "1px solid var(--slate-200)",
              padding: "22px 24px",
              marginBottom: 20,
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    backgroundColor: "#FEF3C7",
                    color: "#B45309",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiBox size={20} />
                </div>
                <div>
                  <h4 style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                    2. Select Items to Return
                  </h4>
                  <div style={{ fontSize: "0.78rem", color: "var(--slate-500)" }}>
                    Check serial items or set bulk quantities to return, OR upload an Excel/CSV sheet
                  </div>
                </div>
              </div>

              {/* Bulk Sheet Upload Actions */}
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  ref={sheetInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleSheetUpload}
                  style={{ display: "none" }}
                />

                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 14px",
                    borderRadius: 6,
                    border: "1px solid var(--slate-300)",
                    backgroundColor: "#fff",
                    color: "var(--slate-700)",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    cursor: "pointer",
                  }}
                >
                  <FiDownload size={13} /> Sample Sheet Template
                </button>

                <button
                  type="button"
                  disabled={parsingSheet || !selectedBillNumber}
                  onClick={() => sheetInputRef.current?.click()}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 16px",
                    borderRadius: 6,
                    border: "none",
                    backgroundColor: selectedBillNumber ? "#15803D" : "var(--slate-400)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    cursor: selectedBillNumber ? "pointer" : "not-allowed",
                  }}
                >
                  <FiUpload size={14} />
                  {parsingSheet ? "Parsing Sheet..." : "Upload Return Sheet (.xlsx / .csv)"}
                </button>
              </div>
            </div>

            {/* Sheet Upload Feedback Banner */}
            {sheetUploadFeedback && (
              <div
                style={{
                  backgroundColor: sheetUploadFeedback.unmatched.length > 0 ? "#FFFBEB" : "#F0FDF4",
                  border: `1px solid ${sheetUploadFeedback.unmatched.length > 0 ? "#FCD34D" : "#86EFAC"}`,
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 16,
                  fontSize: "0.82rem",
                }}
              >
                <div style={{ fontWeight: 700, color: "#1E293B", display: "flex", alignItems: "center", gap: 6 }}>
                  <FiCheckCircle color="#15803D" size={16} />
                  Sheet import processed: {sheetUploadFeedback.matched} item(s) successfully matched and selected for return!
                </div>
                {sheetUploadFeedback.unmatched.length > 0 && (
                  <div style={{ marginTop: 6, color: "#B45309" }}>
                    ⚠️ {sheetUploadFeedback.unmatched.length} row(s) could not be matched with this bill:
                    <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                      {sheetUploadFeedback.unmatched.slice(0, 5).map((u, i) => (
                        <li key={i}>{u.message || `Row #${u.row_index}: ${u.serial_number || u.item_name}`}</li>
                      ))}
                      {sheetUploadFeedback.unmatched.length > 5 && (
                        <li>...and {sheetUploadFeedback.unmatched.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Bill items table */}
            {loadingBillItems ? (
              <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--slate-500)", fontSize: "0.86rem" }}>
                Loading items for Bill #{selectedBillNumber}...
              </div>
            ) : !selectedBillNumber ? (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  backgroundColor: "var(--slate-50)",
                  borderRadius: 8,
                  border: "1.5px dashed var(--slate-200)",
                  color: "var(--slate-400)",
                  fontSize: "0.86rem",
                }}
              >
                <FiTruck size={32} style={{ marginBottom: 8, opacity: 0.5 }} />
                <div>Please enter or select a Bill Number above to view and select items to return.</div>
              </div>
            ) : billItems.length === 0 ? (
              <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--slate-500)", fontSize: "0.86rem" }}>
                No items found under Bill #{selectedBillNumber}.
              </div>
            ) : (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)" }}>
                    Items Under Bill #{selectedBillNumber} ({billItems.length} items total)
                  </span>
                  <button
                    type="button"
                    onClick={toggleSelectAllEligibleSerials}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--primary)",
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Select / Deselect All Eligible Serials
                  </button>
                </div>

                <div style={{ overflowX: "auto", border: "1px solid var(--slate-200)", borderRadius: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                    <thead>
                      <tr style={{ backgroundColor: "var(--slate-100)", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                        <th style={{ padding: "9px 12px", width: 40, textAlign: "center" }}>Return?</th>
                        <th style={{ padding: "9px 12px" }}>Item Description</th>
                        <th style={{ padding: "9px 12px" }}>Serial Number / Type</th>
                        <th style={{ padding: "9px 12px", textAlign: "center" }}>Purchased Qty</th>
                        <th style={{ padding: "9px 12px", textAlign: "center" }}>Return Qty</th>
                        <th style={{ padding: "9px 12px", textAlign: "right" }}>Unit Price</th>
                        <th style={{ padding: "9px 12px" }}>Item Return Reason</th>
                        <th style={{ padding: "9px 12px", width: 140 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billItems.map((it) => {
                        const isSelectedSerial = selectedSerialIds.has(it.id);
                        const curNonSerialQty = nonSerialReturnQty[it.id] || 0;
                        const isRowActive = it.is_serial ? isSelectedSerial : curNonSerialQty > 0;

                        return (
                          <tr
                            key={it.id}
                            style={{
                              borderBottom: "1px solid var(--slate-100)",
                              backgroundColor: isRowActive ? "#FEF3C7" : it.returnable ? "#fff" : "var(--slate-50)",
                              opacity: it.returnable ? 1 : 0.65,
                            }}
                          >
                            {/* Return Checkbox / Indicator */}
                            <td style={{ padding: "9px 12px", textAlign: "center" }}>
                              {it.is_serial ? (
                                <input
                                  type="checkbox"
                                  disabled={!it.returnable}
                                  checked={isSelectedSerial}
                                  onChange={() => toggleSerialSelect(it.id)}
                                  style={{ width: 16, height: 16, cursor: it.returnable ? "pointer" : "not-allowed" }}
                                />
                              ) : (
                                <span style={{ fontSize: "0.74rem", fontWeight: 700, color: curNonSerialQty > 0 ? "#B45309" : "var(--slate-400)" }}>
                                  {curNonSerialQty > 0 ? "✓" : "—"}
                                </span>
                              )}
                            </td>

                            {/* Item Name */}
                            <td style={{ padding: "9px 12px" }}>
                              <div style={{ fontWeight: 700, color: "var(--navy)" }}>{it.item_name}</div>
                              {it.category_name && (
                                <div style={{ fontSize: "0.72rem", color: "var(--slate-400)" }}>
                                  {it.category_name} {it.subcategory_name ? `• ${it.subcategory_name}` : ""}
                                </div>
                              )}
                            </td>

                            {/* Serial Number */}
                            <td style={{ padding: "9px 12px", fontFamily: "monospace" }}>
                              {it.is_serial ? (
                                <span style={{ fontWeight: 700, color: isSelectedSerial ? "#B45309" : "var(--primary)" }}>
                                  {it.serial_number}
                                </span>
                              ) : (
                                <span style={{ color: "var(--slate-400)", fontStyle: "italic", fontSize: "0.75rem" }}>
                                  Non-Serial Bulk
                                </span>
                              )}
                            </td>

                            {/* Purchased Qty */}
                            <td style={{ padding: "9px 12px", textAlign: "center", fontWeight: 600 }}>
                              {it.quantity || 1}
                            </td>

                            {/* Return Qty Input */}
                            <td style={{ padding: "9px 12px", textAlign: "center" }}>
                              {it.is_serial ? (
                                <span style={{ fontWeight: 700 }}>{isSelectedSerial ? 1 : 0}</span>
                              ) : (
                                <input
                                  type="number"
                                  min="0"
                                  max={it.returnable_qty || 0}
                                  disabled={!it.returnable}
                                  value={curNonSerialQty}
                                  onChange={(e) => handleNonSerialQtyChange(it.id, it.returnable_qty, e.target.value)}
                                  style={{
                                    width: 65,
                                    padding: "4px 6px",
                                    textAlign: "center",
                                    borderRadius: 6,
                                    border: curNonSerialQty > 0 ? "1.5px solid #B45309" : "1px solid var(--slate-300)",
                                    fontWeight: 700,
                                    backgroundColor: it.returnable ? "#fff" : "var(--slate-100)",
                                  }}
                                />
                              )}
                            </td>

                            {/* Unit Price */}
                            <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 600 }}>
                              PKR {(parseFloat(it.unit_price) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>

                            {/* Item Reason */}
                            <td style={{ padding: "9px 12px" }}>
                              <input
                                type="text"
                                disabled={!isRowActive}
                                placeholder="Specific reason..."
                                value={itemReasons[it.id] || ""}
                                onChange={(e) => handleItemReasonChange(it.id, e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "4px 8px",
                                  borderRadius: 4,
                                  border: "1px solid var(--slate-300)",
                                  fontSize: "0.76rem",
                                  backgroundColor: isRowActive ? "#fff" : "var(--slate-100)",
                                }}
                              />
                            </td>

                            {/* Status */}
                            <td style={{ padding: "9px 12px" }}>
                              {it.returnable ? (
                                <span style={{ color: "#15803D", fontWeight: 700, fontSize: "0.74rem" }}>
                                  ● Available to Return ({it.returnable_qty})
                                </span>
                              ) : (
                                <span style={{ color: "#DC2626", fontWeight: 600, fontSize: "0.72rem" }}>
                                  ✕ {it.return_blocked_reason || "Not returnable"}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Step 3: Summary & Submission Bar */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg, 12px)",
              border: "1.5px solid #F59E0B",
              padding: "18px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
              boxShadow: "0 4px 15px rgba(245, 158, 11, 0.12)",
            }}
          >
            <div>
              <div style={{ fontSize: "0.85rem", color: "var(--slate-600)" }}>
                Total Items to Return:{" "}
                <strong style={{ color: "var(--navy)", fontSize: "1.05rem" }}>
                  {returnSummary.itemsCount}
                </strong>
              </div>
              <div style={{ fontSize: "1.15rem", fontWeight: 800, color: "#B45309", marginTop: 2 }}>
                Total Refund / Credit: PKR {returnSummary.totalAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                style={{
                  padding: "9px 20px",
                  borderRadius: 8,
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  fontWeight: 600,
                  fontSize: "0.86rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || returnSummary.itemsCount === 0}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 26px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: returnSummary.itemsCount > 0 ? "#B45309" : "var(--slate-400)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  cursor: returnSummary.itemsCount > 0 && !submitting ? "pointer" : "not-allowed",
                  boxShadow: "0 2px 10px rgba(180, 83, 9, 0.25)",
                }}
              >
                <FiCheckCircle size={16} />
                {submitting ? "Recording Return..." : "Submit Purchase Return & Update Inventory"}
              </button>
            </div>
          </div>
        </form>
      ) : (
        /* ========================================================================= */
        /* VIEW: PURCHASE RETURNS HISTORY LIST                                       */
        /* ========================================================================= */
        <div className="fade-in">
          {/* Stats Bar */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: 10,
                border: "1px solid var(--slate-200)",
                padding: "16px 20px",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
                Total Purchase Returns
              </div>
              <div style={{ fontSize: "1.45rem", fontWeight: 800, color: "var(--navy)", marginTop: 4 }}>
                {returns.length}
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: 10,
                border: "1px solid var(--slate-200)",
                padding: "16px 20px",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
                Total Returned Value
              </div>
              <div style={{ fontSize: "1.45rem", fontWeight: 800, color: "#B45309", marginTop: 4 }}>
                PKR {totalReturnAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              border: "1px solid var(--slate-200)",
              padding: "14px 18px",
              marginBottom: 18,
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
              <FiSearch
                size={14}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--slate-400)",
                }}
              />
              <input
                type="text"
                placeholder="Search by Return #, Bill #, Vendor..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 34px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.82rem",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Vendor Filter */}
            <select
              value={selectedVendorFilter}
              onChange={(e) => {
                setSelectedVendorFilter(e.target.value);
                setPage(1);
              }}
              style={{
                padding: "8px 12px",
                borderRadius: 6,
                border: "1px solid var(--slate-300)",
                fontSize: "0.82rem",
                color: "var(--slate-700)",
              }}
            >
              <option value="">All Vendors</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.company_name}
                </option>
              ))}
            </select>

            {/* Date Filters */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem", color: "var(--slate-600)" }}>
              <span>From:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.8rem",
                }}
              />
              <span>To:</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.8rem",
                }}
              />
            </div>

            {(search || selectedVendorFilter || dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setSelectedVendorFilter("");
                  setDateFrom("");
                  setDateTo("");
                  setPage(1);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#B45309",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <FiX size={14} /> Clear Filters
              </button>
            )}
          </div>

          {/* Table */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              border: "1px solid var(--slate-200)",
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.84rem" }}>
                <thead>
                  <tr style={{ backgroundColor: "var(--slate-50)", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                    <th style={{ padding: "12px 14px" }}>Return #</th>
                    <th style={{ padding: "12px 14px" }}>Bill #</th>
                    <th style={{ padding: "12px 14px" }}>Vendor</th>
                    <th style={{ padding: "12px 14px" }}>Return Date</th>
                    <th style={{ padding: "12px 14px", textAlign: "center" }}>Items Count</th>
                    <th style={{ padding: "12px 14px", textAlign: "right" }}>Total Amount</th>
                    <th style={{ padding: "12px 14px" }}>Reason</th>
                    <th style={{ padding: "12px 14px", textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} style={{ padding: "30px", textAlign: "center", color: "var(--slate-400)" }}>
                        Loading purchase returns...
                      </td>
                    </tr>
                  ) : paginatedReturns.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: "40px 20px", textAlign: "center", color: "var(--slate-400)" }}>
                        No purchase returns found matching current filters.
                      </td>
                    </tr>
                  ) : (
                    paginatedReturns.map((ret) => {
                      const itemsCount = ret.items?.reduce((s, it) => s + (parseInt(it.quantity) || 1), 0) || ret.total_items_count || 0;
                      const amt = parseFloat(ret.total_amount) || 0;

                      return (
                        <tr
                          key={ret.id}
                          style={{ borderBottom: "1px solid var(--slate-100)" }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <td style={{ padding: "12px 14px", fontWeight: 700, color: "#B45309" }}>
                            {ret.return_number}
                          </td>
                          <td style={{ padding: "12px 14px", fontFamily: "monospace", color: "var(--navy)" }}>
                            #{ret.bill_number}
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontWeight: 700, color: "var(--navy)" }}>{ret.vendor_name || "—"}</div>
                            {ret.vendor_contact_person && (
                              <div style={{ fontSize: "0.74rem", color: "var(--slate-400)" }}>
                                {ret.vendor_contact_person}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "12px 14px", color: "var(--slate-600)" }}>
                            {ret.return_date || ret.created_at?.slice(0, 10)}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 700 }}>
                            {itemsCount}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", fontWeight: 700, color: "var(--navy)" }}>
                            PKR {amt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: "12px 14px", color: "var(--slate-600)", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {ret.reason || "—"}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "center" }}>
                            <div style={{ display: "inline-flex", gap: 6 }}>
                              <button
                                type="button"
                                title="View / Print Voucher"
                                onClick={() => setViewingReturn(ret)}
                                style={{
                                  padding: "5px 10px",
                                  borderRadius: 6,
                                  border: "1px solid var(--slate-300)",
                                  backgroundColor: "#fff",
                                  color: "var(--primary)",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: "0.78rem",
                                  fontWeight: 600,
                                }}
                              >
                                <FiPrinter size={13} /> Voucher
                              </button>

                              {canEdit && (
                                <button
                                  type="button"
                                  title="Delete Return (Restore Stock)"
                                  onClick={() => setDeleteModal({ open: true, returnItem: ret, deleting: false })}
                                  style={{
                                    padding: "5px 8px",
                                    borderRadius: 6,
                                    border: "1px solid #FCA5A5",
                                    backgroundColor: "#FEF2F2",
                                    color: "#DC2626",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <FiTrash2 size={13} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderTop: "1px solid var(--slate-200)",
                  fontSize: "0.82rem",
                  color: "var(--slate-600)",
                }}
              >
                <div>
                  Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, filteredReturns.length)} of {filteredReturns.length} returns
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: "#fff",
                      cursor: page === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    <FiChevronLeft size={14} />
                  </button>
                  <span style={{ padding: "5px 10px", fontWeight: 700 }}>
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: "#fff",
                      cursor: page === totalPages ? "not-allowed" : "pointer",
                    }}
                  >
                    <FiChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseReturnsTab;
